import React, { useState, useEffect } from "react";
import axiosInstance from "../../../helpers/axios";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import "./AiCreditManager.css";

/**
 * AI Credit Manager — Admin-only panel for viewing balance, assigning credits, and viewing audit log.
 * Only admin/super_admin can see this component.
 */
export default function AiCreditManager() {
  const decode = useCurrentUser();
  const [balance, setBalance] = useState(null);
  const [audit, setAudit] = useState([]);
  const [summary, setSummary] = useState([]);
  const [assignCredits, setAssignCredits] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignMessage, setAssignMessage] = useState(null);
  const [activeTab, setActiveTab] = useState("balance"); // balance | audit | assign
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const role = decode?.role;
  const isAdmin = ["admin", "super_admin"].includes(role);
  const canView = ["admin", "super_admin", "crm"].includes(role);

  useEffect(() => {
    if (!canView) return;
    fetchBalance();
    if (isAdmin) fetchSummary();
  }, []);

  const fetchBalance = async () => {
    try {
      const res = await axiosInstance.get("/admin/ai/credits/balance");
      setBalance(res?.data || null);
    } catch (err) { /* silent */ }
  };

  const fetchAudit = async (from, to) => {
    try {
      let url = "/admin/ai/credits/audit?limit=50";
      if (from) url += `&from=${from}`;
      if (to) url += `&to=${to}`;
      const res = await axiosInstance.get(url);
      setAudit(res?.data?.entries || []);
    } catch (err) { /* silent */ }
  };

  const fetchSummary = async () => {
    try {
      const res = await axiosInstance.get("/admin/ai/credits/summary");
      setSummary(Array.isArray(res?.data) ? res.data : []);
    } catch (err) { /* silent */ }
  };

  const handleAssign = async () => {
    if (!assignCredits || parseInt(assignCredits) <= 0) {
      setAssignMessage({ type: "error", text: "Enter a valid number of credits." });
      return;
    }
    if (!assignReason.trim()) {
      setAssignMessage({ type: "error", text: "Reason is required for audit trail." });
      return;
    }

    setAssignLoading(true);
    setAssignMessage(null);
    try {
      const res = await axiosInstance.post("/admin/ai/credits/assign", {
        credits: parseInt(assignCredits),
        reason: assignReason.trim(),
      });
      if (res?.data?.success) {
        setAssignMessage({ type: "success", text: `${assignCredits} credits assigned successfully.` });
        setAssignCredits("");
        setAssignReason("");
        fetchBalance();
        fetchSummary();
      } else {
        setAssignMessage({ type: "error", text: res?.data?.message || "Failed to assign credits." });
      }
    } catch (err) {
      setAssignMessage({ type: "error", text: err?.response?.data?.message || "Failed to assign credits." });
    } finally {
      setAssignLoading(false);
    }
  };

  if (!canView) return null;

  return (
    <div className="ai-cm">
      <div className="ai-cm-header">
        <h3 className="ai-cm-title">⚡ AI Credit Management</h3>
        <span className="ai-cm-admin-badge">Admin Only</span>
      </div>

      <div className="ai-cm-tabs">
        <button className={`ai-cm-tab ${activeTab === "balance" ? "ai-cm-tab-active" : ""}`} onClick={() => setActiveTab("balance")}>Balance & Usage</button>
        <button className={`ai-cm-tab ${activeTab === "assign" ? "ai-cm-tab-active" : ""}`} onClick={() => setActiveTab("assign")}>Assign Credits</button>
        <button className={`ai-cm-tab ${activeTab === "audit" ? "ai-cm-tab-active" : ""}`} onClick={() => { setActiveTab("audit"); fetchAudit(dateFrom, dateTo); }}>Audit Log</button>
      </div>

      {/* Balance Tab */}
      {activeTab === "balance" && (
        <div className="ai-cm-section">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
            <button onClick={() => { fetchBalance(); if (isAdmin) fetchSummary(); }} style={{ background: "#7c3aed", color: "white", border: "none", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>↻ Refresh</button>
          </div>
          {balance && (
            <div className="ai-cm-balance-cards">
              <div className="ai-cm-balance-card">
                <div className="ai-cm-balance-val">{balance.totalCredits}</div>
                <div className="ai-cm-balance-lbl">Total Allocated</div>
              </div>
              <div className="ai-cm-balance-card">
                <div className="ai-cm-balance-val ai-cm-used">{balance.usedCredits}</div>
                <div className="ai-cm-balance-lbl">Used</div>
              </div>
              <div className="ai-cm-balance-card">
                <div className="ai-cm-balance-val ai-cm-remaining">{balance.remainingCredits}</div>
                <div className="ai-cm-balance-lbl">Remaining</div>
              </div>
            </div>
          )}

          {summary.length > 0 && (
            <>
              <h4 className="ai-cm-sub-title">Usage by User</h4>
              <table className="ai-cm-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Credits Used</th>
                    <th>Actions</th>
                    <th>Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((s, i) => (
                    <tr key={i}>
                      <td>{s._id?.userName || "Unknown"}</td>
                      <td>{s._id?.role || "—"}</td>
                      <td style={{ fontWeight: 600 }}>{s.totalCreditsUsed}</td>
                      <td>{s.actionCount}</td>
                      <td>{s.lastAction ? new Date(s.lastAction).toLocaleDateString("en-IN") : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Assign Tab */}
      {activeTab === "assign" && (
        <div className="ai-cm-section">
          <p className="ai-cm-assign-note">Add credits to your organization's pool. All assignments are logged with your name, timestamp, and reason.</p>
          <div className="ai-cm-assign-form">
            <div className="ai-cm-assign-field">
              <label>Credits to Add</label>
              <input
                type="number"
                min="1"
                value={assignCredits}
                onChange={(e) => setAssignCredits(e.target.value)}
                placeholder="e.g. 500"
                className="ai-cm-input"
              />
            </div>
            <div className="ai-cm-assign-field">
              <label>Reason (required for audit)</label>
              <input
                type="text"
                value={assignReason}
                onChange={(e) => setAssignReason(e.target.value)}
                placeholder="e.g. Monthly top-up, Client request, etc."
                className="ai-cm-input"
              />
            </div>
            <button className="ai-cm-assign-btn" onClick={handleAssign} disabled={assignLoading}>
              {assignLoading ? "Assigning..." : "Assign Credits"}
            </button>
          </div>
          {assignMessage && (
            <div className={`ai-cm-assign-msg ${assignMessage.type === "error" ? "ai-cm-msg-error" : "ai-cm-msg-success"}`}>
              {assignMessage.text}
            </div>
          )}
          <div className="ai-cm-assign-trace">
            <strong>Trace:</strong> Assigned by <em>{decode?.display_name || "Admin"}</em> ({role}) — logged in ai_credit_ledger with action "credits_assigned"
          </div>
        </div>
      )}

      {/* Audit Tab */}
      {activeTab === "audit" && (
        <div className="ai-cm-section">
          <div style={{ display: "flex", gap: "10px", alignItems: "center", marginBottom: "14px" }}>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="ai-cm-input" style={{ width: "160px" }} />
            <span style={{ fontSize: "12px", color: "#667085" }}>to</span>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="ai-cm-input" style={{ width: "160px" }} />
            <button onClick={() => fetchAudit(dateFrom, dateTo)} style={{ background: "#7c3aed", color: "white", border: "none", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>Filter</button>
            <button onClick={() => { setDateFrom(""); setDateTo(""); fetchAudit(); }} style={{ background: "#fff", color: "#667085", border: "1px solid #e0e0e0", padding: "6px 14px", borderRadius: "6px", fontSize: "12px", cursor: "pointer", fontFamily: "inherit" }}>Clear</button>
          </div>
          {audit.length === 0 ? (
            <p style={{ fontSize: "12px", color: "#667085" }}>No audit entries yet.</p>
          ) : (
            <table className="ai-cm-table ai-cm-audit-table">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>User</th>
                  <th>Role</th>
                  <th>Action</th>
                  <th>Credits</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {audit.map((entry, i) => (
                  <tr key={i} className={entry.action === "credits_assigned" ? "ai-cm-audit-assign-row" : ""}>
                    <td>{new Date(entry.actionTimestamp).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td>{entry.userName}</td>
                    <td>{entry.role}</td>
                    <td>
                      <span className={`ai-cm-action-badge ${entry.action === "credits_assigned" ? "ai-cm-action-assign" : "ai-cm-action-use"}`}>
                        {entry.action === "credits_assigned" ? "➕ Assigned" : entry.action}
                      </span>
                    </td>
                    <td style={{ fontWeight: 600, color: entry.creditsUsed < 0 ? "#059669" : "#d32028" }}>
                      {entry.creditsUsed < 0 ? `+${Math.abs(entry.creditsUsed)}` : `-${entry.creditsUsed}`}
                    </td>
                    <td style={{ fontSize: "10px", color: "#667085", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {entry.metadata || (entry.targetUserId ? `User: ${entry.targetUserId}` : "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
